import React from "react";
import {
  Box,
  Grid,
  Typography,
  Chip,
  Paper,
  Link,
} from "@mui/material";
import {
  InsertDriveFileOutlined,
  AccessTimeOutlined,
  CheckCircleOutlined,
  CancelOutlined,
  CheckBoxOutlined,
  PersonOutlined,
  CalendarTodayOutlined,
} from "@mui/icons-material";
import { api_path } from "../../shared.js";

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  1: { label: "Draft",          bgColor: "#9e9e9e", icon: <AccessTimeOutlined sx={{ fontSize: 14 }} /> },
  2: { label: "New",            bgColor: "#ff9800", icon: <AccessTimeOutlined sx={{ fontSize: 14 }} /> },
  3: { label: "Pending Review", bgColor: "#e53935", icon: <CancelOutlined sx={{ fontSize: 14 }} /> },
  4: { label: "Reviewed",       bgColor: "#ed7d31", icon: <AccessTimeOutlined sx={{ fontSize: 14 }} /> },
  5: { label: "Vetted",         bgColor: "#1e88e5", icon: <CheckCircleOutlined sx={{ fontSize: 14 }} /> },
  6: { label: "Approved",       bgColor: "#43a047", icon: <CheckCircleOutlined sx={{ fontSize: 14 }} /> },
  7: { label: "Completed",      bgColor: "#2e7d32", icon: <CheckCircleOutlined sx={{ fontSize: 14 }} /> },
  8: { label: "Reject",         bgColor: "#b71c1c", icon: <CancelOutlined sx={{ fontSize: 14 }} /> },
};

// ─── PageHeader ───────────────────────────────────────────────────────────────
// Props: title, createBy, date, approvalStatus, statusName

export function PageHeader({ title, createBy, date, approvalStatus, statusName }) {
  const statusCfg = STATUS_CONFIG[approvalStatus] ?? { label: statusName, color: "default" };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1.5,
        mb: 3,
      }}
    >
      <Box>
                   
        <Typography variant="h6" fontWeight={600} gutterBottom>
             <label className="head">          {title}</label>

        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {createBy && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
              <PersonOutlined sx={{ fontSize: 15 }} />
              <Typography variant="caption">สร้างโดย: {createBy}</Typography>
            </Box>
          )}
          {date && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
              <CalendarTodayOutlined sx={{ fontSize: 15 }} />
              <Typography variant="caption">{date}</Typography>
            </Box>
          )}
        </Box>
      </Box>
      {approvalStatus !== null && approvalStatus !== undefined && (
        <Chip
          icon={statusCfg.icon}
          label={statusCfg.label || statusName}
          size="small"
          sx={{
            fontWeight: 600,
            borderRadius: "100px",
            bgcolor: statusCfg.bgColor,
            color: "#fff",
            "& .MuiChip-icon": { color: "#fff" },
          }}
        />
      )}
    </Box>
  );
}

// ─── SectionCard ──────────────────────────────────────────────────────────────
// Props: icon, title, children

export function SectionCard({ icon, title, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 2, overflow: "hidden", mb: 2, borderColor: "divider" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          bgcolor: "grey.50",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
        <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
      </Box>
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </Paper>
  );
}

// ─── FieldItem ────────────────────────────────────────────────────────────────
// Props: label, value, xs (default 6)
export function FieldItem({ label, value, xs = 6 }) {
  return (
    <Grid item xs={xs}>
      <label>
        {label}
      </label>
      <p
   
      >
        {value || "–"}
      </p>
    </Grid>
  );
}
// export function FieldItem({ label, value, xs = 6 }) {
//   return (
//     <Grid item xs={xs}>
//       <Typography
//         component="span"
//         variant="caption"
//         sx={{
//           color: "text.secondary",
//           textTransform: "uppercase",
//           fontWeight: "600 !important",
//           fontSize: "0.7rem",
//           display: "block",
//           mb: 0.5,
//         }}
//       >
//         {label}
//       </Typography>
//       <Typography
//         component="span"
//         variant="label"
//         sx={{ display: "block" }}
//         color={value ? "text.primary" : "text.disabled"}
//       >
//         {value || "–"}
//       </Typography>
//     </Grid>
//   );
// }

// ─── MetricCard ───────────────────────────────────────────────────────────────
// Props: label, value, unit

export function MetricCard({ label, value, unit }) {
  return (
    <Box sx={{ bgcolor: "grey.50", borderRadius: 1.5, p: 1.5 }}>
      <Typography
        component="span"
        variant="caption"
        sx={{
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontWeight: 600,
          fontSize: "0.7rem",
          display: "block",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
        <Typography variant="h6" fontWeight={500} color={value ? "text.primary" : "text.disabled"}>
          {value || "–"}
        </Typography>
        {unit && value && (
          <Typography variant="caption" color="text.secondary">{unit}</Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── TagList ──────────────────────────────────────────────────────────────────
// Props: items (string[])

export function TagList({ items }) {
  if (!items || items.length === 0) {
    return <Typography variant="body2" color="text.disabled">–</Typography>;
  }
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {items.map((item, i) => (
        <Box
          key={i}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1.25,
            py: 0.4,
            borderRadius: 1,
            border: "0.5px solid",
            borderColor: "divider",
            bgcolor: "grey.50",
            fontSize: "0.8125rem",
            color: "text.primary",
          }}
        >
          <CheckBoxOutlined sx={{ fontSize: 13, color: "success.main" }} />
          {item}
        </Box>
      ))}
    </Box>
  );
}

// ─── FileGroup ────────────────────────────────────────────────────────────────
// Props: label, files ([{ file_path, file_name }])

export function FileGroup({ label, files = [] }) {
  if (!files || files.length === 0) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        component="span"
        variant="caption"
        sx={{
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontWeight: 600,
          fontSize: "0.7rem",
          display: "block",
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {files.map((f, i) => (
          <Link
            key={i}
            href={`${api_path}/${f.file_path}`}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              color: "primary.main",
              fontSize: "0.8125rem",
              width: "fit-content",
              "&:hover": { bgcolor: "primary.50" },
            }}
          >
            <InsertDriveFileOutlined sx={{ fontSize: 15 }} />
            {f.file_name}
          </Link>
        ))}
      </Box>
    </Box>
  );
}
